import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { and, equals, find } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import Tooltip, { ITooltipProps } from '@ui/atoms/Tooltip';

import Checkbox from '@ui/forms/Checkbox';
import Listbox from '@ui/forms/Listbox';
import TextInput from '@ui/forms/TextInput';

import BackAndNextNav, { IBackAndNextNavProps } from '@ui/molecules/BackAndNextNav';
import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import {
	memberSchema,
	policyholderSchema,
} from '@ui/routes/Insurance/InsuranceDetailsStep/InsuranceDetailsStep.data';

import fullWidth from '@ui/styled/FullWidth';
import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import useMemberInsurance, { IMemberInsurance } from '@hooks/useMemberInsurance';

import { commonLabels } from '@utils/button';
import { doesNotExist, exists } from '@utils/general';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';
import { IInsuranceProviderSelection } from '@providers/InsuranceProvider';

import insuranceRelationships from '@data/insuranceRealtionships';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IMemberData } from '@folx-types/member';

import UPDATE_INSURANCE from '@graphql/mutations//updateInsurance.mutation.gql';
import CREATE_MEMBER_INSURANCE_INFO from '@graphql/mutations/MemberInsuranceInfo/createMemberInsuranceInfo.mutation.gql';

const FullWidthForm = fullWidth(spaced('form'));
const SpacedColumn = spaced<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn));
const PaddedTooltip = padded<ITooltipProps>(Tooltip);
const FullWidthNavigation = fullWidth<IBackAndNextNavProps>(BackAndNextNav);

export interface IInsuranceDetailsStepContent {
	title: string;
	description: string;
	tooltip: string;
	tooltipText: string;
	firstNameLabel: string;
	lastNameLabel: string;
	memberIdLabel: string;
	groupLabel: string;
	isPolicyholderLabel: string;
	policyholderFirstNameLabel: string;
	policyholderLastNameLabel: string;
	policyholderRelationshipLabel: string;
	policyholderDobLabel: string;
}

interface IInsuranceDetailsStepProps extends IFlowStepComponentProps {
	pageContent: IInsuranceDetailsStepContent;
	member: IMemberData;
	provider?: IInsuranceProviderSelection;
	submit?: (id: string, insuredMemberFirstName: string, insuredMemberLastName: string) => void;
	selectedInsurance?: IMemberInsurance;
}

const InsuranceDetailsStep: FC<IInsuranceDetailsStepProps> = ({
	existingFormData,
	handleNavBack = () => {
		return;
	},
	member,
	selectedInsurance,
	pageContent,
	provider,
	stepKey,
	submit,
}) => {
	const [notPolicyHolder, setNotPolicyHolder] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [schema, setSchema] = useState(memberSchema);
	const [insuranceUuid, setInsuranceUuid] = useState<string>('');
	const [isExistingInsurance, setIsExistingInsurance] = useState<boolean | undefined>(undefined);

	const notPolicyHolderSchema = memberSchema.merge(policyholderSchema);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const [updateInsurance] = useMutation(UPDATE_INSURANCE, { client: graphQLv2Client });
	const [createMemberInsuranceInfo] = useMutation(CREATE_MEMBER_INSURANCE_INFO);

	const { memberInsurance } = useMemberInsurance(true, 'network-only');

	const hasSelectedInsurance = exists(selectedInsurance?.id);

	useEffect(() => {
		if (notPolicyHolder) {
			setSchema(notPolicyHolderSchema);
		} else {
			setSchema(memberSchema);
		}
	}, [notPolicyHolder]);

	type Schema = Partial<z.input<typeof notPolicyHolderSchema>>;

	const {
		title,
		description,
		tooltip,
		tooltipText,
		firstNameLabel,
		lastNameLabel,
		memberIdLabel,
		groupLabel,
		isPolicyholderLabel,
		policyholderFirstNameLabel,
		policyholderLastNameLabel,
		policyholderRelationshipLabel,
		policyholderDobLabel,
	} = pageContent;

	const {
		register,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isSubmitted },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			insuredMemberFirstName:
				(existingFormData?.insuredMemberFirstName as string) ?? member?.legalFirstName ?? '',
			insuredMemberLastName:
				(existingFormData?.insuredMemberLastName as string) ?? member?.legalLastName ?? '',
		},
	});

	const memberInsuranceId = watch('memberInsuranceId');

	useDeepCompareEffect(() => {
		if (
			exists(selectedInsurance) ||
			doesNotExist(memberInsurance) ||
			doesNotExist(memberInsuranceId) ||
			doesNotExist(provider?.name)
		) {
			setIsExistingInsurance(false);
			return;
		}

		const existingInsurance = find((insurance) => {
			const matchingInsurance = and(
				equals(insurance.insuranceProviderName, provider?.name),
				equals(insurance.memberInsuranceId, memberInsuranceId)
			);
			return matchingInsurance;
		}, memberInsurance);

		setInsuranceUuid(existingInsurance?.id ?? '');
		setIsExistingInsurance(exists(existingInsurance));
	}, [provider?.name, memberInsurance, memberInsuranceId]);

	useEffect(() => {
		if (hasSelectedInsurance) {
			const memberInsuranceValues = {
				insuredMemberFirstName:
					(existingFormData?.insuredMemberFirstName as string) ?? member?.legalFirstName ?? '',
				insuredMemberLastName:
					(existingFormData?.insuredMemberLastName as string) ?? member?.legalLastName ?? '',
				memberInsuranceId: selectedInsurance?.memberInsuranceId,
				rxGroup: selectedInsurance?.rxGroup,
			};

			if (selectedInsurance?.relationship !== 'SELF') {
				setNotPolicyHolder(true);
				reset({
					...memberInsuranceValues,
					policyholderFirstName: selectedInsurance?.policyHolderFirstName,
					policyholderLastName: selectedInsurance?.policyHolderLastName,
					relationship: selectedInsurance?.relationship,
					policyholderDob: selectedInsurance?.policyHolderDob,
					notPolicyHolder: true,
				});
			} else {
				reset(memberInsuranceValues);
			}
		}
	}, [hasSelectedInsurance]);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);

		const shouldUpdateInsurance = hasSelectedInsurance || isExistingInsurance;

		const isPolicyholderData = {
			insuredMemberFirstName: data.insuredMemberFirstName,
			insuredMemberLastName: data.insuredMemberLastName,
			policyHolderFirstName: data.insuredMemberFirstName,
			policyHolderLastName: data.insuredMemberLastName,
			relationship: 'SELF',
			policyHolderDob: existingFormData?.dob,
		};

		const notPolicyholderData = {
			insuredMemberFirstName: data.insuredMemberFirstName,
			insuredMemberLastName: data.insuredMemberLastName,
			policyHolderFirstName: data.policyholderFirstName,
			policyHolderLastName: data.policyholderLastName,
			relationship: data.relationship,
			policyHolderDob: data.policyholderDob,
		};

		const memberInsuranceData = {
			memberInsuranceId: data.memberInsuranceId,
			rxGroup: data.rxGroup,
		};

		const createMemberInsuranceData = {
			memberUuid: member.id,
			insuranceProviderUuid: hasSelectedInsurance
				? selectedInsurance?.insuranceProviderUuid
				: provider?.id,
			insuranceProviderName: hasSelectedInsurance
				? selectedInsurance?.insuranceProviderName
				: provider?.name,
		};

		const updateMemberInsuranceData = {
			id: selectedInsurance?.id ?? insuranceUuid ?? '',
		};

		const memberInsuranceMutationInput = {
			...memberInsuranceData,
			...(notPolicyHolder ? notPolicyholderData : isPolicyholderData),
			...(shouldUpdateInsurance ? updateMemberInsuranceData : createMemberInsuranceData),
		};

		const memberInsuranceMutation = shouldUpdateInsurance
			? updateInsurance
			: createMemberInsuranceInfo;

		const createData = {
			input: memberInsuranceMutationInput,
		};

		const updateData = {
			clientMutationId: selectedInsurance?.id ?? insuranceUuid ?? '',
			patch: memberInsuranceMutationInput,
		};

		const mutationData = shouldUpdateInsurance ? updateData : createData;

		const memberInsuranceMutationData = await memberInsuranceMutation({
			variables: mutationData,
		}).catch((error) => {
			console.log(error);
			return error;
		});

		const memberInsuranceUuid = shouldUpdateInsurance
			? memberInsuranceMutationData?.data?.updateInsurance?.insurance?.id
			: memberInsuranceMutationData?.data?.createMemberInsuranceInfo?.memberInsuranceInfo?.id;

		setInsuranceUuid(memberInsuranceUuid);

		if (submit) {
			const insuranceId: string = shouldUpdateInsurance
				? memberInsuranceMutationData?.data?.updateInsurance?.insurance?.memberInsuranceId
				: memberInsuranceMutationData?.data?.createMemberInsuranceInfo?.memberInsuranceInfo
						?.memberInsuranceId;

			submit(
				insuranceId,
				(data?.insuredMemberFirstName as string) ?? member.legalFirstName ?? '',
				(data?.insuredMemberLastName as string) ?? member.legalLastName ?? ''
			);
		}

		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<HeadingText>{title}</HeadingText>
				<BasicText>
					{description}
					<PaddedTooltip
						defaultIcon
						title={tooltip}
						description={tooltipText}
						padding="none"></PaddedTooltip>
				</BasicText>
			</SpacedColumn>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)} marginBottom="xxxl">
				<SpacedColumn gap="md" marginBottom="xl">
					<TextInput
						placeholder=""
						label={firstNameLabel}
						error={isSubmitted && !!errors.insuredMemberFirstName}
						errorLabel={errors?.insuredMemberFirstName?.message ?? ''}
						{...register('insuredMemberFirstName')}
						type="text"
						fullWidth
					/>
					<TextInput
						placeholder=""
						label={lastNameLabel}
						error={isSubmitted && !!errors.insuredMemberLastName}
						errorLabel={errors?.insuredMemberLastName?.message ?? ''}
						{...register('insuredMemberLastName')}
						type="text"
						fullWidth
					/>
					<TextInput
						placeholder=""
						label={memberIdLabel}
						error={isSubmitted && !!errors.memberInsuranceId}
						errorLabel={errors?.memberInsuranceId?.message ?? ''}
						{...register('memberInsuranceId')}
						type="text"
						fullWidth
					/>
					<TextInput
						placeholder=""
						label={groupLabel}
						error={isSubmitted && !!errors.rxGroup}
						errorLabel={errors?.rxGroup?.message ?? ''}
						{...register('rxGroup')}
						type="text"
						fullWidth
					/>
					<Checkbox
						onClick={() => setNotPolicyHolder(!notPolicyHolder)}
						textSize="xxs"
						label={isPolicyholderLabel}
						{...register('notPolicyHolder')}
					/>
					{notPolicyHolder && (
						<>
							<TextInput
								placeholder=""
								label={policyholderFirstNameLabel}
								error={isSubmitted && !!errors.policyholderFirstName}
								errorLabel={errors?.policyholderFirstName?.message ?? ''}
								{...register('policyholderFirstName')}
								type="text"
								fullWidth
							/>
							<TextInput
								placeholder=""
								label={policyholderLastNameLabel}
								error={isSubmitted && !!errors.policyholderLastName}
								errorLabel={errors?.policyholderLastName?.message ?? ''}
								{...register('policyholderLastName')}
								type="text"
								fullWidth
							/>
							<Controller
								control={control}
								name="relationship"
								render={({ field }) => (
									<Listbox
										label={policyholderRelationshipLabel}
										error={!!errors?.relationship}
										errorLabel={errors?.relationship?.message}
										defaultOption="default"
										options={[{ value: 'default', label: 'Select' }, ...insuranceRelationships]}
										{...field}
									/>
								)}
							/>
							<TextInput
								placeholder="MM/DD/YYYY"
								label={policyholderDobLabel}
								error={!!errors.policyholderDob}
								errorLabel={errors?.policyholderDob?.message ?? ''}
								{...register('policyholderDob')}
								type="date"
								fullWidth
							/>
						</>
					)}
				</SpacedColumn>
				<FullWidthNavigation
					backOnClick={() => handleNavBack(stepKey)}
					submitLabel={commonLabels.next}
					submitting={submitting}
				/>
			</FullWidthForm>
		</CenteredGridItem>
	);
};

export default InsuranceDetailsStep;
